import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MapPin, 
  Eye, 
  Shield, 
  Key, 
  Lock, 
  Globe,
  ChevronRight,
  CheckCircle2,
  Star,
  ExternalLink,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Toolkit() {
  const { user } = useAuth();
  const [guides, setGuides] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedGuide, setSelectedGuide] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [guidesRes, productsRes] = await Promise.all([
        axios.get(`${API}/guides`),
        axios.get(`${API}/products`)
      ]);
      setGuides(guidesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Failed to load toolkit data');
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: 'All Guides', icon: Shield },
    { id: 'tracking', label: 'Tracking', icon: Eye },
    { id: 'location', label: 'Location', icon: MapPin },
    { id: 'vpn', label: 'VPN', icon: Globe },
    { id: 'encryption', label: 'Encryption', icon: Lock },
    { id: 'browser', label: 'Browser', icon: Globe },
  ];

  const filteredGuides = activeCategory === 'all' 
    ? guides 
    : guides.filter(g => g.category === activeCategory);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'medium': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'advanced': return 'bg-red-500/10 text-red-500 border-red-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const getCategoryIcon = (category) => {
    const cat = categories.find(c => c.id === category);
    return cat?.icon || Shield;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="toolkit-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl text-white mb-2">
            Privacy Toolkit
          </h1>
          <p className="text-gray-500">
            Step-by-step guides and recommended tools to protect your digital privacy
          </p>
        </div>

        <Tabs defaultValue="guides" className="space-y-6">
          <TabsList className="bg-[#0a0a0a] border border-[#262626] p-1">
            <TabsTrigger value="guides" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black" data-testid="tab-guides">
              Protection Guides
            </TabsTrigger>
            <TabsTrigger value="tools" className="data-[state=active]:bg-[#00FF94] data-[state=active]:text-black" data-testid="tab-tools">
              Recommended Tools
            </TabsTrigger>
          </TabsList>

          {/* Guides Tab */}
          <TabsContent value="guides" className="space-y-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeCategory === cat.id
                      ? 'bg-[#00FF94] text-black'
                      : 'bg-[#0a0a0a] border border-[#262626] text-gray-400 hover:text-white hover:border-[#00FF94]/30'
                  }`}
                  data-testid={`category-${cat.id}`}
                >
                  <cat.icon className="w-4 h-4" />
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Guides Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredGuides.map((guide, index) => {
                const Icon = getCategoryIcon(guide.category);
                return (
                  <Card 
                    key={guide.id} 
                    className={`bg-[#0a0a0a] border-[#262626] overflow-hidden group card-hover cursor-pointer ${guide.is_premium && !user?.is_premium ? 'opacity-60' : ''}`}
                    onClick={() => setSelectedGuide(guide)}
                    data-testid={`guide-card-${index}`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-[#00FF94]" />
                        </div>
                        <div className="flex items-center gap-2">
                          {guide.is_premium && (
                            <Badge className="bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20">
                              Premium
                            </Badge>
                          )}
                          <Badge className={getDifficultyColor(guide.difficulty)}>
                            {guide.difficulty}
                          </Badge>
                        </div>
                      </div>
                      <CardTitle className="text-white text-lg mt-4">{guide.title}</CardTitle>
                      <CardDescription className="text-gray-500">{guide.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <p className="text-xs text-gray-500 uppercase tracking-wider font-mono">Steps</p>
                        <ul className="space-y-2">
                          {guide.steps.slice(0, 3).map((step, stepIndex) => (
                            <li key={stepIndex} className="flex items-start gap-2 text-sm text-gray-400">
                              <CheckCircle2 className="w-4 h-4 text-[#00FF94] flex-shrink-0 mt-0.5" />
                              <span>{step}</span>
                            </li>
                          ))}
                          {guide.steps.length > 3 && (
                            <li className="text-sm text-gray-500 pl-6">
                              +{guide.steps.length - 3} more steps...
                            </li>
                          )}
                        </ul>
                      </div>
                      
                      {guide.is_premium && !user?.is_premium && (
                        <div className="mt-4 p-3 bg-[#00FF94]/5 border border-[#00FF94]/20 rounded-lg">
                          <p className="text-xs text-[#00FF94]">Upgrade to Premium to access this guide</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Tools Tab */}
          <TabsContent value="tools" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product, index) => (
                <Card 
                  key={product.id}
                  className="bg-[#0a0a0a] border-[#262626] overflow-hidden group card-hover"
                  data-testid={`product-card-${index}`}
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                    <Badge className="absolute top-3 right-3 bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20 capitalize">
                      {product.category.replace('_', ' ')}
                    </Badge>
                  </div>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-heading font-semibold text-white text-lg">{product.name}</h3>
                      <div className="flex items-center gap-1 text-yellow-500">
                        <Star className="w-4 h-4 fill-current" />
                        <span className="text-sm font-mono">{product.rating}</span>
                      </div>
                    </div>
                    <p className="text-gray-500 text-sm mb-4">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[#00FF94]">{product.price}</span>
                      <a 
                        href={product.affiliate_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-gray-400 hover:text-[#00FF94] transition-colors"
                      >
                        Learn more
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-6 mt-8">
              <p className="text-xs text-gray-500 text-center">
                <span className="text-[#00FF94]">Affiliate Disclosure:</span> Some links on this page are affiliate links. 
                We may earn a commission if you make a purchase through these links, at no extra cost to you.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Guide Modal */}
      {selectedGuide && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedGuide(null)}
        >
          <div 
            className="bg-[#0a0a0a] border border-[#262626] rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#0a0a0a] border-b border-[#262626] p-6 flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center flex-shrink-0">
                  {(() => {
                    const Icon = getCategoryIcon(selectedGuide.category);
                    return <Icon className="w-6 h-6 text-[#00FF94]" />;
                  })()}
                </div>
                <div>
                  <h2 className="text-2xl font-heading font-bold text-white mb-2">{selectedGuide.title}</h2>
                  <p className="text-gray-500 mb-3">{selectedGuide.description}</p>
                  <div className="flex items-center gap-3 text-sm">
                    <Badge className={getDifficultyColor(selectedGuide.difficulty)}>
                      {selectedGuide.difficulty}
                    </Badge>
                    {selectedGuide.is_premium && (
                      <Badge className="bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20">
                        Premium
                      </Badge>
                    )}
                    <span className="text-gray-500">{selectedGuide.category}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedGuide(null)}
                className="text-gray-500 hover:text-white transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="p-6">
              {selectedGuide.is_premium && !user?.is_premium ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center mb-4">
                    <Lock className="w-8 h-8 text-[#00FF94]" />
                  </div>
                  <h3 className="text-white font-bold text-xl mb-2">Premium Guide</h3>
                  <p className="text-gray-400 mb-6">Upgrade to Premium to access this step-by-step guide</p>
                  <a 
                    href="/pricing" 
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#00FF94] text-black rounded-lg font-medium hover:bg-[#00FF94]/90 transition-colors"
                  >
                    View Pricing
                    <ChevronRight className="w-4 h-4" />
                  </a>
                </div>
              ) : (
                <div className="space-y-4">
                  <h3 className="text-white font-semibold text-lg mb-4">Step-by-Step Instructions:</h3>
                  <ol className="space-y-4">
                    {selectedGuide.steps.map((step, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-[#00FF94] font-mono text-sm">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-300 leading-relaxed">{step}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
